import { useEffect, useMemo, useState } from 'react';

// LIBS
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// SERVICES
import phoneMaskArray from 'js/components/LoginForms/phoneMaskArray.json';
import { usePartnerUserForms } from 'js/contexts/PartnerUserFormsContext';
import { useAuth } from 'js/contexts/AuthContext';
import cfac22 from 'js/library/utils/cfac22';
import {
  confirmDate,
  isValidDate,
  toStoredUploadPath,
  trackEventMatomoVisit,
} from 'js/library/utils/helpers';

// STYLES
import { Box } from '@mui/material';

// API
import { uploadFiles } from 'js/library/utils/API/uploadFiles';
import { processPreData } from './processPreData';
import { mapFormByMode } from 'js/library/utils/formVisibility';

// PACKAGE
import { CreateUserPackage } from '@sysmobil/synoro-packages';

const colorText = (color) => color || '#000000';

export default function CreateUser() {
  const {
    form: partnerForm,
    partnerId,
    indexKey,
    loading: partnerFormLoading,
    error: partnerFormError,
    reload: reloadPartnerForm,
  } = usePartnerUserForms();

  const { setRegistrationDraft, authState } = useAuth();
  const isUserValidated = true;
  const form = partnerForm;
  const accessToken = authState.identify?.identifier;
  const preData = authState.currentUserInfo;
  const setFormData = setRegistrationDraft;
  const seuClubePersist = { partnerId, clubeId: partnerId, customizations: {} };
  const navigate = useNavigate();

  useEffect(() => {
    if (!authState.identify?.identifier) navigate('/login', { replace: true });
  }, [authState.identify?.identifier, navigate]);

  const visibleForm = useMemo(() => (form ? mapFormByMode(form, 'register') : null), [form]);

  const [valuesForm, setValuesForm] = useState({});
  const [formFieldsFilter, setFormFieldsFilter] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const [files, setFiles] = useState({});
  const [fileImage, setFileImage] = useState({}); //avatar
  const [fieldErrors, setFieldErrors] = useState({ formFields: {}, otherBlocks: {} });

  useEffect(() => {
    if (!isUserValidated) {
      navigate('/validarAcesso');
    }
  }, [isUserValidated, navigate]);

  useEffect(() => {
    if (visibleForm?.formFields?.length > 0 || visibleForm?.otherBlocks) {
      const mainFieldsFiltered = visibleForm.formFields.filter(
        (field) => field.fieldName !== 'cellPhone' && !field.indexKey
      );
      setFormFieldsFilter(mainFieldsFiltered);
    }
  }, [visibleForm?.formFields, visibleForm?.otherBlocks]);

  // preData
  useEffect(() => {
    const hasValidPreData =
      !!preData &&
      (preData.documentList?.length > 0 ||
        preData.contactList?.length > 0 ||
        preData.aliasName ||
        preData.name ||
        preData.addressList?.length > 0 ||
        preData.otherBlocks);

    if (!hasValidPreData) return;

    const newValues = processPreData({
      preData,
      valuesForm,
      form,
      accessToken,
    });

    setValuesForm(newValues);
    // eslint-disable-next-line
  }, [preData, form?.formFields, form?.otherBlocks, accessToken]);

  useEffect(() => {
    trackEventMatomoVisit('Cadastro');
  }, []);

  const handleInputChange = (event, input) => {
    let value = event.target.value;
    const name = event.target.name;
    const checked = event.target.checked;

    if (name === 'zipCode' && value.length > 5) {
      value = value.replace(/\D/g, '');
      value = value.replace(/(\d{5})(\d{1,3})/, '$1-$2');
    }

    if (input.inputType === 'date' && value) {
      const [year, month, day] = value.split('-');
      value = `${day}/${month}/${year}`;
    }

    const updatedForm = { ...valuesForm };

    switch (input.type) {
      case 'root':
      case 'others': {
        const sectionKey = input.sectionKey || 'formfields';

        if (sectionKey === 'formfields') {
          if (input.inputType === 'date') {
            updatedForm[name] = value;
          } else if (input.fieldType === 'checkbox') {
            updatedForm[name] = checked;
          } else {
            updatedForm[name] = value;
          }
        } else {
          // É um checkbox ou outro campo dentro de otherBlocks
          const otherBlocks = { ...updatedForm.otherBlocks };
          const section = otherBlocks[sectionKey] || {};

          if (input.inputType === 'date') {
            section[name] = value;
          } else if (input.fieldType === 'checkbox') {
            section[name] = checked;
          } else {
            section[name] = value;
          }

          otherBlocks[sectionKey] = section;
          updatedForm.otherBlocks = otherBlocks;
        }

        break;
      }

      case 'addressList': {
        if (input.sectionKey === 'formfields') {
          // Endereço da seção fixa
          const addressList = [...(updatedForm.addressList || [])];
          const idx = addressList.findIndex((a) => a.type === input.fieldName);
          const fallbackIdx =
            idx === -1 && addressList.length === 1 && !addressList[0].type ? 0 : idx;
          const updatedAddress = {
            ...(fallbackIdx >= 0 ? addressList[fallbackIdx] : {}),
            type: input.fieldName,
            [name]: value,
          };

          if (fallbackIdx >= 0) {
            addressList[fallbackIdx] = updatedAddress;
          } else {
            addressList.push(updatedAddress);
          }

          updatedForm.addressList = addressList;
        } else {
          // Endereço de outras seções (otherBlocks)
          const sectionKey = input.sectionKey;
          const otherBlocks = { ...updatedForm.otherBlocks };
          const section = otherBlocks[sectionKey] || {};
          const addressList = [...(section.addressList || [])];

          const idx = addressList.findIndex((a) => a.type === input.fieldName);
          const fallbackIdx =
            idx === -1 && addressList.length === 1 && !addressList[0].type ? 0 : idx;
          const updatedAddress = {
            ...(fallbackIdx >= 0 ? addressList[fallbackIdx] : {}),
            type: input.fieldName,
            [name]: value,
          };

          if (fallbackIdx >= 0) {
            addressList[fallbackIdx] = updatedAddress;
          } else {
            addressList.push(updatedAddress);
          }

          otherBlocks[sectionKey] = {
            ...section,
            addressList,
          };

          updatedForm.otherBlocks = otherBlocks;
        }
        break;
      }

      case 'contactList': {
        const sectionKey = input.sectionKey || 'formfields';
        const updatedForm = { ...valuesForm };

        // Tratamento específico para telefone
        if (input.fieldType === 'cellPhone') {
          const cleanedValue = value.replace(/\D/g, '');
          const sectionKey = input.sectionKey || 'formfields';
          const isRoot = sectionKey === 'formfields';

          // Busca contato atual para manter a DDI (mask.ddd)
          const currentContact = isRoot
            ? valuesForm.contactList?.find((item) => item.type === input.fieldName)
            : valuesForm.otherBlocks?.[sectionKey]?.contactList?.find(
              (item) => item.type === input.fieldName
            );

          const currentMask = currentContact?.mask || phoneMaskArray.find((m) => m.ddd === '+55');

          const newContact = {
            type: input.fieldName,
            phone: cleanedValue,
            value: `${currentMask.ddd}${cleanedValue}`,
            mask: currentMask,
          };

          if (isRoot) {
            const contactList = [...(valuesForm.contactList || [])];
            const index = contactList.findIndex((item) => item.type === input.fieldName);
            if (index >= 0) contactList[index] = newContact;
            else contactList.push(newContact);
            setValuesForm({ ...valuesForm, contactList });
          } else {
            const otherBlocks = { ...valuesForm.otherBlocks };
            const section = otherBlocks[sectionKey] || {};
            const contactList = [...(section.contactList || [])];
            const index = contactList.findIndex((item) => item.type === input.fieldName);
            if (index >= 0) contactList[index] = newContact;
            else contactList.push(newContact);
            section.contactList = contactList;
            otherBlocks[sectionKey] = section;
            setValuesForm({ ...valuesForm, otherBlocks });
          }

          setFieldErrors?.((prev = {}) => {
            const next = {
              formFields: { ...(prev.formFields || {}) },
              otherBlocks: { ...(prev.otherBlocks || {}) },
            };

            if (sectionKey === 'formfields') {
              delete next.formFields[input.fieldName];
            } else {
              next.otherBlocks[sectionKey] = { ...(next.otherBlocks[sectionKey] || {}) };
              delete next.otherBlocks[sectionKey][input.fieldName];
              if (Object.keys(next.otherBlocks[sectionKey]).length === 0) {
                delete next.otherBlocks[sectionKey];
              }
            }
            return next;
          });

          return;
        }

        // Tratamento genérico para outros tipos de contato, como email
        if (input.fieldType === 'email') {
          const newContact = {
            type: input.fieldName,
            value: value,
          };

          if (sectionKey === 'formfields') {
            const contactList = [...(updatedForm.contactList || [])];
            const index = contactList.findIndex((item) => item.type === input.fieldName);

            if (index >= 0) {
              contactList[index] = newContact;
            } else {
              contactList.push(newContact);
            }

            updatedForm.contactList = contactList;
          } else {
            const otherBlocks = { ...(updatedForm.otherBlocks || {}) };
            const section = otherBlocks[sectionKey] || {};
            const contactList = [...(section.contactList || [])];
            const index = contactList.findIndex((item) => item.type === input.fieldName);

            if (index >= 0) {
              contactList[index] = newContact;
            } else {
              contactList.push(newContact);
            }

            section.contactList = contactList;
            otherBlocks[sectionKey] = section;
            updatedForm.otherBlocks = otherBlocks;
          }

          setFieldErrors?.((prev = {}) => {
            const next = {
              formFields: { ...(prev.formFields || {}) },
              otherBlocks: { ...(prev.otherBlocks || {}) },
            };

            if (sectionKey === 'formfields') {
              delete next.formFields[input.fieldName];
            } else {
              next.otherBlocks[sectionKey] = { ...(next.otherBlocks[sectionKey] || {}) };
              delete next.otherBlocks[sectionKey][input.fieldName];
              if (Object.keys(next.otherBlocks[sectionKey]).length === 0) {
                delete next.otherBlocks[sectionKey];
              }
            }
            return next;
          });

          setValuesForm(updatedForm);
          return;
        }

        break;
      }

      case 'documentList': {
        const sectionKey = input.sectionKey || 'formfields';

        const cleanedValue = value.replace(/\D/g, '');
        const newDoc = { type: input.fieldName, value: cleanedValue };

        if (sectionKey === 'formfields') {
          const documentList = [...(updatedForm.documentList || [])];
          const index = documentList.findIndex((item) => item.type === input.fieldName);

          if (index >= 0) documentList[index] = newDoc;
          else documentList.push(newDoc);

          updatedForm.documentList = documentList;
        } else {
          const otherBlocks = { ...updatedForm.otherBlocks };
          const section = otherBlocks[sectionKey] || {};
          const documentList = [...(section.documentList || [])];
          const index = documentList.findIndex((item) => item.type === input.fieldName);

          if (index >= 0) documentList[index] = newDoc;
          else documentList.push(newDoc);

          section.documentList = documentList;
          otherBlocks[sectionKey] = section;
          updatedForm.otherBlocks = otherBlocks;
        }

        break;
      }

      case 'optInList': {
        const sectionKey = input.sectionKey || 'formfields';

        // Busca o optIn existente para preservar o optInId original
        const existingOptInList =
          sectionKey === 'formfields'
            ? valuesForm.optInList || []
            : valuesForm.otherBlocks?.[sectionKey]?.optInList || [];

        const existingOptIn = existingOptInList.find(
          (item) =>
            item.optInId === input.optInId ||
            item.optInId === `${input.fieldName}_${sectionKey}` ||
            item.optInId === input.fieldName
        );

        // Usa o optInId existente ou cria um novo seguindo a mesma lógica da API
        const optInId =
          existingOptIn?.optInId || input.optInId || `${input.fieldName}_${sectionKey}`;

        const newOpt = {
          ...(existingOptIn || {}), // Preserva todas as propriedades existentes
          accept: checked,
          optInId: optInId,
          type: input.label,
          version: 1,
          ...(checked ? { dateAcceptance: Date.now() } : {}),
        };

        if (sectionKey === 'formfields') {
          const optInList = [...(updatedForm.optInList || [])];
          const index = optInList.findIndex(
            (item) =>
              item.optInId === optInId ||
              item.optInId === `${input.fieldName}_${sectionKey}` ||
              item.optInId === input.fieldName
          );

          if (index >= 0) {
            optInList[index] = newOpt;
          } else {
            optInList.push(newOpt);
          }

          updatedForm.optInList = optInList;
        } else {
          const otherBlocks = { ...updatedForm.otherBlocks };
          const section = otherBlocks[sectionKey] || {};
          const optInList = [...(section.optInList || [])];

          const index = optInList.findIndex(
            (item) =>
              item.optInId === optInId ||
              item.optInId === `${input.fieldName}_${sectionKey}` ||
              item.optInId === input.fieldName
          );

          if (index >= 0) {
            optInList[index] = newOpt;
          } else {
            optInList.push(newOpt);
          }

          section.optInList = optInList;
          otherBlocks[sectionKey] = section;
          updatedForm.otherBlocks = otherBlocks;
        }

        break;
      }

      default: {
        const sectionKey = input.sectionKey || 'formfields';

        if (sectionKey === 'formfields') {
          updatedForm[name] = value;
        } else {
          updatedForm.otherBlocks = {
            ...(updatedForm.otherBlocks || {}),
            [sectionKey]: {
              ...(updatedForm.otherBlocks?.[sectionKey] || {}),
              [name]: value,
            },
          };
        }

        break;
      }
    }

    // Limpa erro do campo ao alterar valor
    setFieldErrors?.((prev = {}) => {
      const next = {
        formFields: { ...(prev.formFields || {}) },
        otherBlocks: { ...(prev.otherBlocks || {}) },
      };

      const sectionKey = input.sectionKey || 'formfields';

      if (sectionKey === 'formfields') {
        if (next.formFields[input.fieldName]) {
          delete next.formFields[input.fieldName];
        }
      } else {
        next.otherBlocks[sectionKey] = { ...(next.otherBlocks[sectionKey] || {}) };
        if (next.otherBlocks[sectionKey][input.fieldName]) {
          delete next.otherBlocks[sectionKey][input.fieldName];
        }
        if (Object.keys(next.otherBlocks[sectionKey]).length === 0) {
          delete next.otherBlocks[sectionKey];
        }
      }

      return next;
    });

    setValuesForm(updatedForm);
  };

  const handleSubmit = async () => {
    setLoading(true);

    let stopIteration = false;

    const formatDateToTimestamp = (dateStr) => {
      if (typeof dateStr !== 'string') return null;

      const parts = dateStr.split('/');
      if (parts.length !== 3) return null;

      const [day, month, year] = parts;
      if (!day || !month || !year) return null;

      const date = new Date(`${year}-${month}-${day}`);
      return isNaN(date.getTime()) ? null : date.getTime();
    };

    // Cria cópia profunda para não alterar o state original
    const userInfoObj = JSON.parse(JSON.stringify(valuesForm));

    const missingRequired = (formFieldsFilter || []).filter((field) => {
      if (!field.active || !field.required || ['edit', 'none'].includes(field.visibilityType))
        return false;
      const name = field.fieldName;
      if (field.type === 'optInList') {
        const id = field.optInId || `${name}_formfields`;
        const opt = valuesForm.optInList?.find(
          (item) => item.optInId === id || item.optInId === name
        );
        return !opt || opt.accept !== true;
      }
      if (field.type === 'contactList')
        return !valuesForm.contactList?.find((item) => item.type === name)?.value?.trim();
      if (field.type === 'documentList')
        return !valuesForm.documentList
          ?.find((item) => item.type === name)
          ?.value?.toString()
          .trim();
      if (field.type === 'addressList') {
        const address = valuesForm.addressList?.find((item) => item.type === name);
        return (
          !address?.zipCode ||
          !address?.streetAve ||
          !(address.streetNumber || address.number) ||
          !address?.city ||
          !address?.state
        );
      }
      if (field.fieldType === 'checkbox') return valuesForm[name] !== true;
      const value = valuesForm[name];
      return (
        value == null ||
        (typeof value === 'string' && !value.trim()) ||
        (Array.isArray(value) && value.length === 0)
      );
    });

    if (missingRequired.length > 0) {
      setLoading(false);
      const errors = missingRequired.reduce((result, field) => {
        result[field.fieldName] = true;
        return result;
      }, {});
      setFieldErrors((previous) => ({
        ...previous,
        formFields: { ...(previous.formFields || {}), ...errors },
      }));
      toast.warn('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (Array.isArray(userInfoObj.contactList)) {
      userInfoObj.contactList = userInfoObj.contactList.map(({ type, value }) => ({
        type,
        value,
      }));
    }

    // seção fixa (formFields)
    formFieldsFilter?.forEach((field) => {
      if (stopIteration) return;

      if (field.type === 'root' || field.type === 'others') {
        if (field.fieldType === 'date') {
          const rawDate = userInfoObj[field.fieldName];
          if (!confirmDate(rawDate)) {
            setLoading(false);
            toast.warn('Por favor, insira uma data válida!');
            stopIteration = true;
            return;
          }
          userInfoObj[field.fieldName] = formatDateToTimestamp(rawDate);
        }
      }

      // Validação de celular
      if (field.type === 'contactList' && field.fieldType === 'cellPhone') {
        const rawCell = valuesForm.contactList?.find((c) => c.type === 'cellPhone');

        // O usuário ainda não digitou ou não tem telefone
        if (!rawCell?.value) {
          setLoading(false);
          return toast.warn('Número de celular inválido.');
        }

        // Extrai o DDI do próprio campo de celular (vindo de handleCountryChange)
        const mask = rawCell.mask || phoneMaskArray.find((m) => m.ddd === '+55');
        const cleanedPhone = rawCell.value.replace(/\D/g, ''); // Remove tudo que não é número

        // Remove DDI do número completo para comparar só o "número local"
        const phoneWithoutDDD = cleanedPhone.replace(mask.ddd.replace('+', ''), '');

        // Define o comprimento esperado (sem o DDI)
        const expectedLength = mask.mask.replace(/\D/g, '').length;

        // Valida o número
        if (phoneWithoutDDD.length < expectedLength) {
          setLoading(false);
          return toast.warn('Número de celular inválido.');
        }
      }

      // CPF
      if (field.type === 'documentList' && field.fieldName === 'cpf') {
        const doc = (valuesForm.documentList || []).find((d) => d.type === 'cpf');
        if (!doc?.value || doc.value.replace(/\D/g, '').length < 11) {
          setLoading(false);
          toast.warn('Número de CPF inválido.');
          stopIteration = true;
          return;
        }
      }

      // CNPJ
      if (field.type === 'documentList' && field.fieldName === 'cnpj') {
        const doc = (valuesForm.documentList || []).find((d) => d.type === 'cnpj');
        if (doc && doc.value?.replace(/\D/g, '').length < 14) {
          setLoading(false);
          toast.warn('Número de CNPJ inválido.');
          stopIteration = true;
          return;
        }
      }
    });

    // ——— Validação dinâmica de formFields obrigatórios ———
    if (formFieldsFilter) {
      const missingFixed = [];

      formFieldsFilter?.forEach((field) => {
        if (!field.active || !field.required) return;
        if (field.visibilityType === 'edit' || field.visibilityType === 'none') return;

        const name = field.fieldName;

        // 1) contactList (celular, e-mail, etc.)
        if (field.type === 'contactList') {
          const list = valuesForm.contactList || [];
          const item = list.find((c) => c.type === name);
          if (!item?.value) {
            missingFixed.push(name);
          } else if (field.fieldType === 'email') {
            // validação básica de formato de e-mail
            const re = /^\S+@\S+\.\S+$/;
            if (!re.test(item.value)) missingFixed.push(name);
          }
          return;
        }

        // 2) documentList (CPF, CNPJ, RG, RGIA, etc.)
        if (field.type === 'documentList') {
          const list = valuesForm.documentList || [];
          const doc = list.find((d) => d.type === name);
          if (!doc?.value) {
            missingFixed.push(name);
          } else {
            const digits = doc.value.replace(/\D/g, '');
            if (field.fieldType === 'cpf' && digits.length < 11) {
              missingFixed.push(name);
            } else if (field.fieldType === 'cnpj' && digits.length < 14) {
              missingFixed.push(name);
            }
            // para 'rg', 'rgia' e outros tipos de documento, basta existir valor
          }
          return;
        }

        // 3) addressList: precisa haver item e CEP válido
        if (field.type === 'addressList') {
          const arr = valuesForm.addressList || [];
          if (!Array.isArray(arr) || arr.length === 0) {
            missingFixed.push(name);
          } else {
            const entry =
              arr.find((a) => a.type === field.fieldName) ??
              (arr.length === 1 && !arr[0].type ? arr[0] : {});
            const zip = entry.zipCode || '';
            if (zip.replace(/\D/g, '').length < 8) {
              missingFixed.push(name);
            }
          }
          return;
        }

        // 4) optInList: ao menos um item
        if (field.type === 'optInList') {
          const arr = valuesForm.optInList || [];
          if (!Array.isArray(arr) || arr.length === 0) {
            missingFixed.push(name);
          }
          return;
        }

        // 5) checkbox
        if (field.fieldType === 'checkbox') {
          if (!valuesForm[name]) missingFixed.push(name);
          return;
        }

        // 6) arquivo/avatar
        if (['file', 'avatar'].includes(field.fieldType)) {
          let hasFile = false;

          if (field.fieldType === 'file') {
            const fileArray = files?.formfields?.[name];
            hasFile = Array.isArray(fileArray) && fileArray.length > 0;
          } else if (field.fieldType === 'avatar') {
            const avatarData = fileImage?.formfields?.[name];
            hasFile = avatarData && avatarData !== 'removed';
          }

          if (!hasFile) missingFixed.push(name);

          return;
        }

        // 7) date
        if (field.fieldType === 'date') {
          if (!isValidDate(valuesForm[name])) missingFixed.push(name);
          return;
        }

        // 8) select / selectMultiple / maritalStatus
        if (
          field.fieldType === 'select' ||
          field.inputType === 'select' ||
          field.inputType === 'selectMultiple' ||
          field.fieldType === 'maritalStatus' ||
          field.inputType === 'maritalStatus'
        ) {
          const val = valuesForm[name];

          // selectMultiple must be a non-empty array
          if (field.inputType === 'selectMultiple') {
            if (!Array.isArray(val) || val.length === 0) {
              missingFixed.push(name);
            }
            return;
          }

          // single selects / maritalStatus must be non-empty string/value
          if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) {
            missingFixed.push(name);
          }
          return;
        }

        // 9) campos simples (root, others)
        const val = valuesForm[name];
        if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) {
          missingFixed.push(name);
        }
      });

      if (missingFixed.length > 0) {
        console.log('[validation] missingFixed:', missingFixed);
        setLoading(false);
        toast.warn(
          'Por favor, preencha todos os campos obrigatórios da seção: "Dados Cadastrais".'
        );
        setTabValue(0);
        setFieldErrors((prev) => ({
          ...prev,
          formFields: missingFixed.reduce((m, key) => {
            m[key] = true;
            return m;
          }, {}),
        }));
        return;
      }
    }

    // seções extras (otherBlocks)
    if (visibleForm.otherBlocks) {
      for (const [blockKey, block] of Object.entries(visibleForm.otherBlocks)) {
        if (!block.active || !block.fields?.length) continue;

        for (const field of block.fields) {
          if (field.fieldType === 'date') {
            const rawDate = valuesForm.otherBlocks?.[blockKey]?.[field.fieldName];

            userInfoObj.otherBlocks ??= {};
            userInfoObj.otherBlocks[blockKey] ??= {};
            userInfoObj.otherBlocks[blockKey][field.fieldName] = formatDateToTimestamp(rawDate);
            continue;
          }
        }
      }
    }

    // Validação de CEP
    const cep = userInfoObj.addressList?.[0]?.zipCode;
    if (cep && cep.replace(/\D/g, '').length < 8) {
      setLoading(false);
      return toast.warn('O CEP precisa ter 8 dígitos.');
    }

    // ——— Validação dinâmica de otherblocks obrigatórios ———
    if (visibleForm.otherBlocks) {
      for (const [blockKey, blockConfig] of Object.entries(visibleForm.otherBlocks)) {
        if (!blockConfig.active || !blockConfig.fields?.length) continue;
        const valuesBlock = valuesForm.otherBlocks?.[blockKey] || {};
        const missing = [];

        blockConfig.fields.forEach((field) => {
          if (!field.active || !field.required) return;
          const name = field.fieldName;
          const val = valuesBlock[name];

          // 1) addressList
          if (field.type === 'addressList') {
            const arr = valuesBlock.addressList || [];
            if (!arr.length) {
              missing.push(name);
            } else {
              const entry =
                arr.find((a) => a.type === field.fieldName) ??
                (arr.length === 1 && !arr[0].type ? arr[0] : {});
              const zip = entry.zipCode || '';
              if (zip.replace(/\D/g, '').length < 8) missing.push(name);
            }
            return;
          }

          // 2) contactList (celular / email)
          if (field.type === 'contactList') {
            const contact = (valuesBlock.contactList || []).find((c) => c.type === name);
            if (field.fieldType === 'cellPhone') {
              if (!contact?.value) {
                missing.push(name);
              } else {
                const mask = contact.mask || phoneMaskArray.find((m) => m.ddd === '+55');
                const digits = contact.value.replace(/\D/g, '');
                const local = digits.replace(mask.ddd.replace('+', ''), '');
                const expected = mask.mask.replace(/\D/g, '').length;
                if (local.length < expected) missing.push(name);
              }
            } else if (field.fieldType === 'email') {
              if (!contact?.value?.trim()) missing.push(name);
            } else {
              if (!contact) missing.push(name);
            }
            return;
          }

          // 3) documentList (CPF / CNPJ / RG)
          if (field.type === 'documentList') {
            const doc = (valuesBlock.documentList || []).find((d) => d.type === name);
            const raw = doc?.value?.replace(/\D/g, '') || '';
            if (!raw) {
              missing.push(name);
            } else if (field.fieldType === 'cpf' && raw.length < 11) {
              missing.push(name);
            } else if (field.fieldType === 'cnpj' && raw.length < 14) {
              missing.push(name);
            } else if (field.fieldType === 'rg' && raw.length < 1) {
              missing.push(name);
            }
            return;
          }

          // 4) optInList
          if (field.type === 'optInList') {
            const arr = valuesBlock.optInList || [];
            if (!arr.some((o) => o.accept)) missing.push(name);
            return;
          }

          // 5) checkbox
          if (field.fieldType === 'checkbox') {
            if (!val) missing.push(name);
            return;
          }

          // 6) file
          if (field.fieldType === 'file') {
            const saved = valuesBlock[name];
            const hasSaved = saved && saved.url;
            const newFiles = files?.[blockKey]?.[name] || [];
            if (!hasSaved && !newFiles.length) missing.push(name);
            return;
          }

          // 7) avatar
          if (field.fieldType === 'avatar') {
            const saved = valuesBlock[name];
            const hasSaved = typeof saved === 'string' && saved.trim() !== '';
            const uploaded = fileImage?.[blockKey]?.[name];
            const hasNew = uploaded && uploaded !== 'removed';
            if (!hasSaved && !hasNew) missing.push(name);
            return;
          }

          // 8) select / selectMultiple / maritalStatus (otherBlocks)
          if (
            field.fieldType === 'select' ||
            field.inputType === 'select' ||
            field.inputType === 'selectMultiple' ||
            field.fieldType === 'maritalStatus' ||
            field.inputType === 'maritalStatus'
          ) {
            const valueBlock = valuesBlock[name];

            if (field.inputType === 'selectMultiple') {
              if (!Array.isArray(valueBlock) || valueBlock.length === 0) {
                missing.push(name);
              }
              return;
            }

            if (
              valueBlock === undefined ||
              valueBlock === null ||
              (typeof valueBlock === 'string' && valueBlock.trim() === '')
            ) {
              missing.push(name);
            }
            return;
          }

          // 9) date
          if (field.fieldType === 'date') {
            if (!isValidDate(val)) missing.push(name);
            return;
          }

          // 10) number
          if (field.fieldType === 'number' || field.inputType === 'number') {
            if (val === undefined || val === null || val === '') missing.push(name);
            return;
          }

          // 11) text simples
          if (field.type === 'text' || field.fieldType === 'text' || field.inputType === 'text') {
            if (!val || (typeof val === 'string' && val.trim() === '')) {
              missing.push(name);
            }
            return;
          }

          // 12) fallback
          if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) {
            missing.push(name);
          }
        });

        if (missing.length > 0) {
          setLoading(false);
          toast.warn(
            `Por favor, preencha todos os campos obrigatórios da seção: "${blockConfig.label}".`
          );

          const allBlocks = Object.entries(visibleForm.otherBlocks)
            .filter(
              ([_, block]) =>
                block.active &&
                block.fields?.length > 0 &&
                (block.sectionType === 'predefined' || block.onEdit !== 'not-visible')
            )
            .map(([key]) => key);

          const tabIndex = allBlocks.indexOf(blockKey);
          setTabValue(tabIndex + 1); // redireciona para a aba com erro

          setFieldErrors((prev) => ({
            ...prev,
            otherBlocks: {
              ...prev.otherBlocks,
              [blockKey]: missing.reduce((err, key) => {
                err[key] = true;
                return err;
              }, {}),
            },
          }));
          return;
        }
      }
    }

    // >>>>>>>>>>>> UPLOAD DE ARQUIVOS (APÓS VALIDAÇÕES) <<<<<<<<<<<<<<<

    // Campos fixos (formFields) - file
    for (const field of formFieldsFilter) {
      if (stopIteration) break;

      if (field.type === 'root' || field.type === 'others') {
        if (field.fieldType === 'file') {
          try {
            const fieldName = field.fieldName;
            const fileArray = files?.formfields?.[fieldName];

            if (fileArray?.length > 0) {
              const fileData = fileArray[0];
              const mimeType = fileData.mimeType || fileData.file?.mimeType;
              const buffer = fileData.buffer || fileData.file?.buffer;
              const fileName = fileData.name || fileData.file?.name || fieldName;

              let result;

              if (mimeType) {
                result = await uploadFiles('filesSeuClube', {
                  mimeType,
                  fileName,
                  buffer,
                });
              }

              if (result) {
                userInfoObj[fieldName] = {
                  name: fileName,
                  url: toStoredUploadPath(result),
                };
              }
            }
          } catch (error) {
            console.error(`Erro ao processar arquivo ${field.fieldName}:`, error);
            userInfoObj[field.fieldName] = valuesForm[field.fieldName];
          }
        }

        // Campos fixos (formFields) - avatar
        if (field.fieldType === 'avatar') {
          try {
            const fieldName = field.fieldName;
            const avatarData = fileImage?.formfields?.[fieldName];

            if (avatarData === 'removed') {
              userInfoObj[fieldName] = '';
            } else if (avatarData) {
              const mimeType = avatarData.mimeType;
              const buffer = avatarData.buffer;

              let result;

              if (mimeType) {
                result = await uploadFiles('userAvatar', {
                  mimeType,
                  fileName: `${valuesForm?.aliasName ?? 'user'}_${seuClubePersist.clubeId
                    }_${fieldName}`,
                  buffer,
                });
              }

              userInfoObj[fieldName] = result || valuesForm[fieldName];
            }
          } catch (error) {
            console.error(`Erro ao processar avatar ${field.fieldName}:`, error);
            userInfoObj[field.fieldName] = valuesForm[field.fieldName];
          }
        }
      }
    }

    // Seções extras (otherBlocks) - avatar e file
    if (visibleForm.otherBlocks) {
      for (const [blockKey, block] of Object.entries(visibleForm.otherBlocks)) {
        if (!block.active || !block.fields?.length) continue;

        for (const field of block.fields) {
          if (field.fieldType === 'avatar') {
            try {
              const imageData =
                blockKey === 'formfields'
                  ? fileImage?.[field.fieldName]
                  : fileImage?.[blockKey]?.[field.fieldName];

              const isRemoved = imageData === 'removed';

              if (isRemoved || imageData) {
                userInfoObj.otherBlocks ??= {};
                userInfoObj.otherBlocks[blockKey] ??= {};

                if (isRemoved) {
                  userInfoObj.otherBlocks[blockKey][field.fieldName] = '';
                } else {
                  if (imageData.mimeType) {
                    const result = await uploadFiles('userAvatar', {
                      mimeType: imageData.mimeType,
                      fileName: `${valuesForm?.aliasName ?? valuesForm?.name}_${seuClubePersist.clubeId
                        }_${field.fieldName}`,
                      buffer: imageData.buffer,
                    });

                    userInfoObj.otherBlocks[blockKey][field.fieldName] = result;
                  }
                }
              }
            } catch (err) {
              console.warn('Falha ao enviar avatar de otherBlock:', err);
            }
          }

          if (field.fieldType === 'file') {
            try {
              const fileArray =
                blockKey === 'formfields'
                  ? files?.[field.fieldName]
                  : files?.[blockKey]?.[field.fieldName];

              if (Array.isArray(fileArray) && fileArray.length > 0) {
                const { file, name } = fileArray[0];

                let result;

                if (file.mimeType) {
                  result = await uploadFiles('filesSeuClube', {
                    mimeType: file.mimeType,
                    fileName: `${name}`,
                    buffer: file.buffer,
                  });
                }

                if (result) {
                  userInfoObj.otherBlocks ??= {};
                  userInfoObj.otherBlocks[blockKey] ??= {};
                  userInfoObj.otherBlocks[blockKey][field.fieldName] = {
                    name,
                    url: toStoredUploadPath(result),
                  };
                }
              }
            } catch {
              setLoading(false);
              return toast.error('Erro ao enviar arquivo complementar.');
            }
          }
        }
      }
    }

    // Metadados exigidos pelo fluxo de partnerUserForms em produção.
    userInfoObj.partnerId = seuClubePersist.partnerId;
    userInfoObj.partnerName = seuClubePersist.clubeId;
    userInfoObj.updateDate = new Date().getTime();

    // Garante que campos com indexKey sejam incluídos no payload
    const indexKeyFields = (visibleForm?.formFields || []).filter(
      (f) => f.indexKey === true && f.active !== false
    );

    indexKeyFields.forEach((field) => {
      const { fieldName, type } = field;

      // Para campos documentList, garante que existam na estrutura
      if (type === 'documentList' && accessToken) {
        userInfoObj.documentList = userInfoObj.documentList || [];
        const existingDoc = userInfoObj.documentList.find((d) => d.type === fieldName);
        if (!existingDoc) {
          userInfoObj.documentList.push({
            type: fieldName,
            value: accessToken,
          });
        } else if (!existingDoc.value) {
          existingDoc.value = accessToken;
        }
        return;
      }

      // Para campos contactList, garante que existam na estrutura
      if (type === 'contactList' && accessToken) {
        userInfoObj.contactList = userInfoObj.contactList || [];
        const existingContact = userInfoObj.contactList.find((c) => c.type === fieldName);
        if (!existingContact) {
          userInfoObj.contactList.push({
            type: fieldName,
            value: accessToken,
          });
        } else if (!existingContact.value) {
          existingContact.value = accessToken;
        }
        return;
      }

      // Para campos root/others, adiciona o accessToken se não existir
      if ((type === 'root' || type === 'others') && accessToken) {
        if (!userInfoObj[fieldName]) {
          userInfoObj[fieldName] = accessToken;
        }
      }
    });

    if (userInfoObj.otherBlocks && visibleForm.otherBlocks) {
      Object.entries(visibleForm.otherBlocks).forEach(([blockKey, blockConfig]) => {
        if (!blockConfig.active || !blockConfig.fields?.length) return;

        // Busca os nomes dos campos de telefone celular deste bloco
        const cellPhoneFieldNames = blockConfig.fields
          .filter((field) => field.type === 'contactList' && field.fieldType === 'cellPhone')
          .map((field) => field.fieldName);

        if (!cellPhoneFieldNames.length) return;

        const contactList = userInfoObj.otherBlocks?.[blockKey]?.contactList || [];

        const updatedContactList = contactList.map((contact) => {
          if (cellPhoneFieldNames.includes(contact.type)) {
            return {
              type: contact.type,
              value: `${contact.mask?.ddd || '+55'}${contact.phone || ''}`,
            };
          }
          return contact;
        });

        userInfoObj.otherBlocks[blockKey].contactList = updatedContactList;
      });
    }

    // Preserve the complete dynamic form payload for the cellphone verification step.
    userInfoObj.dynamicData = JSON.parse(JSON.stringify(userInfoObj));
    userInfoObj.dynamicFields = formFieldsFilter;
    userInfoObj.indexKey = indexKey?.fieldName || indexKey?.fieldName || indexKey;

    // Limpa campos vazios
    const cleanedPayload = JSON.parse(
      JSON.stringify(userInfoObj, (_, value) => {
        if (value === '' || value === null || value === undefined) return undefined;
        return value;
      })
    );
    if (stopIteration) return;

    setFormData(cleanedPayload);
    navigate('/informarCelular');
  };

  if (partnerFormLoading) {
    return (
      <Box className="paper-container" role="status" sx={{ mb: 1, textAlign: 'center', py: 4 }}>
        Carregando...
      </Box>
    );
  }

  if (partnerFormError || !partnerForm) {
    return (
      <Box className="paper-container" role="alert" sx={{ mb: 1, textAlign: 'center', py: 4 }}>
        <p>{partnerFormError || 'Não foi possível carregar o formulário.'}</p>
        <button type="button" onClick={reloadPartnerForm}>
          Tentar novamente
        </button>
      </Box>
    );
  }

  return (
    <Box className="paper-container" sx={{ mb: 1 }}>
      <CreateUserPackage
        ambient={cfac22('ambient')}
        project="seuClube"
        title="Cadastre-se"
        form={visibleForm}
        formFieldsFilter={formFieldsFilter}
        valuesForm={valuesForm}
        setValuesForm={setValuesForm}
        tabValue={tabValue}
        setTabValue={setTabValue}
        seuClubePersist={seuClubePersist}
        colorText={colorText}
        loading={loading}
        handleInputChange={handleInputChange}
        phoneMaskArray={phoneMaskArray}
        handleSubmit={handleSubmit}
        files={files}
        setFiles={setFiles}
        fileImage={fileImage}
        setFileImage={setFileImage}
        fieldErrors={fieldErrors}
        setFieldErrors={setFieldErrors}
      />
    </Box>
  );
}
